import { DOCUMENT, Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CbreAlertService } from '@cbre/alerts';
import { HelpComponent } from '@core/dialogs/help/help.component';
import { ICatalog } from '@shared/models/icommons.model';
import { IUser } from '@shared/models/iuser.model';
import { AppStorageService } from '@shared/services/app-storage.service';
import { L10nTranslationService } from 'angular-l10n';
import { tap, Observable, concat } from 'rxjs';
import { DataService } from './data.service';
import { DialogService } from './dialog.service';
import { LoadingStateService } from './loading-state.service';
import { SharedService } from './shared.service';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@Injectable({
  providedIn: 'root',
})
export class OpenHelpDialogService {
  constructor(
    private shared: SharedService,
    private notification: DialogService,
    private cbreAlertService: CbreAlertService,
    private storage: AppStorageService,
    private dataService: DataService,
    private location: Location,
    private loadingStateService: LoadingStateService,
    public translationService: L10nTranslationService,
    @Inject(CbreLoggerToken) private logger: CbreLogger,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private appCatalogs: ICatalog[] = [];

  public getCanvas(options: { [key: string]: any } = {}): Promise<void> {
    const url = this.location.path(true);
    this.loadingStateService.resolving();
    // Two delay(0) hops exist before the loader is visually painted:
    // 1. miqLoadingIndicatorModeChange directive's delay(0) inserts the component into the DOM.
    // 2. LoadingIndicatorComponent's own delay(0) sets the className that makes it visible.
    // A 100ms timeout clears both async layers + Angular CD + browser paint reliably.
    return new Promise<void>(resolve => setTimeout(resolve, 100))
      .then(async () => (await import('html2canvas')).default(this.document.body, { useCORS: true }))
      .then(canvas => {
        canvas.id = 'canvasId';
        this.#getPagesCatalog(canvas, url, options);
      })
      .catch(err => this.logger.error(`error canvas: ${err}`))
      .finally(() => this.loadingStateService.resolved());
  }

  #getPagesCatalog(canvas, _url: string, options) {
    let appCatalogs$;
    if (this.appCatalogs?.length === 0) {
      // if catalogs are not yet loaded then load and then open dialog
      appCatalogs$ = concat([this.#loadCatalogs$(), this.#openDialog(canvas, options)]);
    } else {
      appCatalogs$ = this.#openDialog(canvas, options);
    }

    appCatalogs$.subscribe(closeEvent => {
      if (closeEvent === 'navigate') {
        this.dataService.setHelpClosed();
        this.cbreAlertService.showSuccessSnackBar_l10n('shared.yourServiceTicketCreatedSuccessfully');
      }
    });
  }

  #loadCatalogs$(): Observable<ICatalog[]> {
    return this.shared.getAppCatalog().pipe(tap(catalogs => (this.appCatalogs = catalogs)));
  }

  #openDialog(canvas, options) {
    const data = { canvas, userInfo: this.storage.getItem<IUser>('userInfo'), options };
    return this.notification.openDialog(HelpComponent, data, { panelClass: 'filter-modals' });
  }
}
